<template>
	<v-app id="app">
		<Navigation v-if="isLoggedIn" />
		<v-main :class="isLoggedIn ? 'bg-light-gray' : null">
			<v-container
				class="wrapper"
				:fluid="!isLoggedIn || isFullWidthRoute"
				:style="isLoggedIn == false ? 'background: #fff;' : null"
			>
				<router-view></router-view>
			</v-container>
		</v-main>
		
		<!-- Global Components -->
		<EnvironmentIndicator />
		<GlobalShippingDialog v-if="isLoggedIn" />
		<HardwareStatus v-if="isLoggedIn && shouldShowHardware" />

		<!-- Global Toast/Snackbar Stack -->
		<div class="toast-stack">
			<v-snackbar
				v-for="(toast, index) in toastStack"
				:key="toast.id"
				v-model="toast.show"
				:timeout="toast.timeout"
				:color="toast.color"
				top
				right
				:multi-line="toast.multiline"
				:style="{ marginTop: `${70 + (index * 80)}px` }"
				class="global-toast"
				@input="onToastClose(toast.id, $event)"
			>
				{{ toast.message }}
				<template v-slot:action="{ attrs }">
					<v-btn
						color="white"
						text
						v-bind="attrs"
						@click="closeToast(toast.id)"
					>
						Close
					</v-btn>
				</template>
			</v-snackbar>
		</div>
	</v-app>
</template>

<script>
import Vue from "vue";
import Navigation from "@/components/Navigation.vue";
import EnvironmentIndicator from "@/components/EnvironmentIndicator.vue";
import GlobalShippingDialog from "@/components/Shipping/GlobalShippingDialog.vue";
import HardwareStatus from "@/components/Hardware/HardwareStatus.vue";
import { getScannerEventService } from "@/services/ScannerEventService";
import { getKeyboardWedgeScanner } from "@/services/KeyboardWedgeScanner";
import { resetHardwareService } from "@/services/HardwareIntegrationService";
import { shouldEnableHardware } from "@/models/User";

export default {
	components: {
		Navigation,
		EnvironmentIndicator,
		GlobalShippingDialog,
		HardwareStatus,
	},
	data() {
		return {
			scannerService: null,
			keyboardWedgeScanner: null,
			toastStack: [],
			toastIdCounter: 0,
			recentToasts: new Map()
		};
	},
	async mounted() {
		// Setup global toast system
		Vue.prototype.$toast = {
			success: (message, options) => this.showToast(message, 'success', options),
			error: (message, options) => this.showToast(message, 'error', options),
			info: (message, options) => this.showToast(message, 'info', options),
			warning: (message, options) => this.showToast(message, 'warning', options)
		};

		if (this.$store.getters.isLoggedIn) {
			await this.initializeApp();
		}

		// Watch for login state changes
		this.$store.watch(
			(state) => state.authentication?.isLoggedIn,
			async (isLoggedIn) => {
				if (isLoggedIn) {
					await this.initializeApp();
				} else {
					this.cleanup();
				}
			}
		);
	},
	computed: {
		isLoggedIn() {
			return this.$store.getters.isLoggedIn;
		},
		shouldShowHardware() {
			const user = this.$store.getters.userInfo;
			if (!user) return false;
			return shouldEnableHardware(user);
		},
		// Routes that should use full width (fluid container)
		isFullWidthRoute() {
			const fullWidthRoutes = ['/reports'];
			return fullWidthRoutes.some(route => this.$route.path.startsWith(route));
		},
	},
	methods: {
		async initializeApp() {
			try {
				// Existing initialization
				await this.$store.dispatch("refreshUserInfo");
				await this.$store.dispatch("retrievesystemCategories", this);
				this.$systemCategories.load(this);
				
				// Initialize global hardware services (only for users with hardware permissions)
				if (this.shouldShowHardware && !this.scannerService) {
					// Check if hardware is enabled in environment or user settings
					const envEnabled = process.env.VUE_APP_HARDWARE_ENABLED !== 'false';
					const userEnabled = localStorage.getItem('hardware_enabled') !== 'false';
					
					if (envEnabled && userEnabled) {
						this.scannerService = getScannerEventService();
						await this.scannerService.initialize();
						console.log('[App] Scanner service initialized globally');
						
						// Also start keyboard wedge scanner for scanners that act as keyboards
						this.keyboardWedgeScanner = getKeyboardWedgeScanner();
						this.keyboardWedgeScanner.start();
						console.log('[App] Keyboard wedge scanner started');
					}
				}
			} catch (error) {
				console.error("App initialization failed:", error);
				
				// Only logout if it's a session error
				if (error.message?.includes('session') || error.message?.includes('unauthorized')) {
					await this.$store.dispatch("logout");
					if (this.$route.path !== "/login") {
						this.$router.push("/login");
					}
				}
			}
		},
		
		cleanup() {
			if (this.scannerService) {
				this.scannerService.destroy();
				this.scannerService = null;
			}
			if (this.keyboardWedgeScanner) {
				this.keyboardWedgeScanner.stop();
				this.keyboardWedgeScanner = null;
			}
			// Destroy hardware service singleton to close WebSocket and reset state.
			// Prevents a previous user's hardware session from leaking into the next login.
			resetHardwareService();
		},

		showToast(message, type = 'info', options = {}) {
			const toastKey = `${type}:${message}`;
			const now = Date.now();
			const duplicateWindow = 2000;

			if (this.recentToasts.has(toastKey)) {
				const lastShown = this.recentToasts.get(toastKey);
				if (now - lastShown < duplicateWindow) {
					console.log('[Toast] Duplicate toast blocked:', message);
					return;
				}
			}

			this.recentToasts.set(toastKey, now);

			const toastId = ++this.toastIdCounter;
			const toast = {
				id: toastId,
				message: message,
				color: type,
				timeout: options.timeout || 5000,
				multiline: (message.length > 60) || options.multiline || false,
				show: true
			};

			this.toastStack.unshift(toast);

			setTimeout(() => {
				this.removeToast(toastId);
			}, toast.timeout + 500);

			setTimeout(() => {
				this.recentToasts.delete(toastKey);
			}, duplicateWindow);
		},

		closeToast(toastId) {
			const toast = this.toastStack.find(t => t.id === toastId);
			if (toast) {
				toast.show = false;
			}
		},

		onToastClose(toastId, isVisible) {
			if (!isVisible) {
				this.removeToast(toastId);
			}
		},

		removeToast(toastId) {
			const index = this.toastStack.findIndex(t => t.id === toastId);
			if (index > -1) {
				this.toastStack.splice(index, 1);
			}
		}
	},
	beforeDestroy() {
		this.cleanup();
	}
};
</script>

<!-- GLOBAL STYLES -->
<style lang="scss">
@import "./sass/main.scss";

/* Global Toast Styles */
.toast-stack {
	position: fixed;
	top: 0;
	right: 0;
	z-index: 9999;
	pointer-events: none;
	width: 0 !important;  /* Don't take up any space */
	height: 0 !important;
	overflow: visible !important;

	.global-toast {
		position: fixed !important;  /* Change from static to fixed */
		top: 0 !important;
		right: 0 !important;
		width: auto !important;
		pointer-events: none !important;  /* Disable for the container */

		.v-snack__wrapper {
			pointer-events: auto !important;  /* Only the wrapper is clickable */
			margin-right: 16px !important;
			margin-bottom: 8px !important;
			min-width: 320px;
			max-width: 450px;
			border-radius: 8px !important;
			box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15) !important;
			transition: all 0.3s ease !important;
		}
	}
}

.global-toast {

	/* Improve text readability */
	.v-snack__content {
		color: #ffffff !important;
		font-weight: 500 !important;
		font-size: 14px !important;
		line-height: 1.4 !important;
		padding: 16px !important;
	}

	/* Multi-line support */
	&.v-snack--multi-line .v-snack__wrapper {
		min-height: auto;
	}

	/* Close button styling */
	.v-btn {
		color: rgba(255, 255, 255, 0.9) !important;
		font-weight: 600 !important;

		&:hover {
			background-color: rgba(255, 255, 255, 0.1) !important;
		}
	}

	/* Different colors for different message types with better contrast */
	&.success .v-snack__wrapper {
		background: linear-gradient(135deg, #4CAF50 0%, #45a049 100%) !important;
	}

	&.error .v-snack__wrapper {
		background: linear-gradient(135deg, #f44336 0%, #d32f2f 100%) !important;
	}

	&.warning .v-snack__wrapper {
		background: linear-gradient(135deg, #FF9800 0%, #f57c00 100%) !important;
	}

	&.info .v-snack__wrapper {
		background: linear-gradient(135deg, #2196F3 0%, #1976d2 100%) !important;
	}
}

/* Global Print Styles - Hide non-printable elements */
@media print {
	/* Hide the hardware status widget */
	.hardware-status-new,
	.hardware-status-widget,
	.hardware-widget {
		display: none !important;
	}

	/* Hide navigation and environment indicator */
	.v-navigation-drawer,
	.environment-indicator,
	.toast-stack,
	.v-snackbar {
		display: none !important;
	}

	/* Hide app bar if present */
	.v-app-bar {
		display: none !important;
	}
}
</style>
